import { Calendar, Check, Circle, CircleChevronDown, Clock, DollarSign, FileText } from "lucide-react";
import React, { useEffect } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@workspace/web-ui/components/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@workspace/web-ui/components/avatar";
import { Badge } from "@workspace/web-ui/components/badge";
import { Button } from "@workspace/web-ui/components/button";
import { Card, CardContent } from "@workspace/web-ui/components/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/web-ui/components/tabs";

import { useSuspenseQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { usePaymentCheckoutMutation } from "~/features/payments/api/mutations";
import { useAuth } from "~/providers/auth.provider";
import { EmptyTodo, EmptyCompletedTodo } from "../../components/empty-todo";
import SnapshotCards from "../../components/snapshot-card";
import SubNav from "../../components/sub-nav";
import { accordionItems } from "../../data/accordion-data";
import { activeMedicationsQueryOptions, medicalsAllergiesQueryOptions, medicalsConditionsQueryOptions } from "../medicals/api/queries";
import { tasksQueryOptions } from "./api/queries";
import { groupTasksByMonth, getTasksWithReadableMonths } from "./lib/task";
import { InputData, Task, MonthData } from "./types";
import { MedicationRefillRequestModal } from "../../components/medication-request";
import { MedicationPaymentModal } from "../../components/medication-payment";
import { MedicationRefillPaymentModal } from "../../components/medication-refill-payment";
import { formatFullDate, formatTime } from "@workspace/lib/date";
import { useCreateRegistrationPaymentMutation } from "~/features/appointments/api/mutations/consultation";
import { useTaskStore } from "../../store/task";
import { formatDate } from "~/lib/date-utils";
// import { mockUserTasksMultiMonth } from "./data";

export function WeightLossPage() {
  const tasksQuery = useSuspenseQuery(tasksQueryOptions());
  const medicalsAllergiesQuery = useSuspenseQuery(medicalsAllergiesQueryOptions());
  const medicalsConditionsQuery = useSuspenseQuery(medicalsConditionsQueryOptions());
  const activeMedicationsQuery = useSuspenseQuery(activeMedicationsQueryOptions());

  const paymentCheckoutMutation = usePaymentCheckoutMutation();

  const router = useRouter()

  // const taskStatus = (mockUserTasksMultiMonth || {}) as InputData;
  const taskStatus = (tasksQuery.data?.task_status || {}) as InputData;

  // console.log("WeightLossPage", tasksQuery.data?.task_status)
  const auth = useAuth();
  const { user } = useAuth();
  const [payForMed, setPayForMed] = React.useState(false);
  const [payForRefill, setPayForRefill] = React.useState(false);
  const [requestRefill, setRequestRefill] = React.useState(false);
  const [activeItem, setActiveItem] = React.useState("");
  const [activeTodoTask, setActiveTodoTask] = React.useState("");
  const [activeCompletedTask, setActiveCompletedTask] = React.useState("");

  const [prescriptionId, setPrescriptionId] = React.useState("");
  const [reqPrescriptionId, setReqPrescriptionId] = React.useState("");
  const [reqMedicationId, setReqMedicationId] = React.useState("");
  const [refillPrescriptionId, setRefillPrescriptionId] = React.useState("");
  const [refillMedicationId, setRefillMedicationId] = React.useState("");
  const { mutate: createRegistrationPayment, isPending } = useCreateRegistrationPaymentMutation();
  const {setTaskCount} = useTaskStore()

  // Group tasks by month and get them in readable format
  const groupedTasks = groupTasksByMonth(taskStatus);
  const tasksWithReadableMonths = getTasksWithReadableMonths(groupedTasks);

  const sortedMonths: MonthData[] = Object.entries(tasksWithReadableMonths)
    .sort((a, b) => {
      const monthA = new Date(a[0]).getTime();
      const monthB = new Date(b[0]).getTime();
      return monthA - monthB;
    })
    .map(([monthName, tasks]) => ({
      month: monthName,
      data: tasks
    }));

    useEffect(() => {
      setTaskCount(sortedMonths.length)
    }, [sortedMonths.length, setTaskCount])

  const completedStages = sortedMonths.map(monthData =>
    monthData.data.every((task: Task) => task.completed)
  );

  const successUrl = (appointment_id: string) => `${window.location.origin}/dashboard/weight-loss/appointments/confirmation?appointment_id=${appointment_id}`;
  const errorUrl = (appointment_id: string) => `${window.location.origin}/dashboard/weight-loss/appointments/error?appointment_id=${appointment_id}`;
  const consultSuccessUrl = `${window.location.origin}/consultations/confirmation`;
  const consultCancelUrl = `${window.location.origin}/consultations/error`;

  function formatAppointmentText(text: string) {
    const monthName = new Date(user?.registration_date || "").toLocaleString('en-US', { month: 'long' });
    const formattedText = text.replace(/\(Month \d+\)/, `(${monthName})`);
    return formattedText;
  }

  const renderTaskActionButton = (task: Task) => {
    // Join appointment meeting
    if (task.type === "Appointment" && task.meeting_url && !task.reschedule && !task.payment_required) {
      return (
        <a
          href={task.meeting_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center h-9 justify-center rounded-lg bg-[#4272DD] px-4 py-2 text-sm font-medium text-white hover:bg-[#3661C5]"
        >
          Join Meeting
        </a>
      );
    }

    // Reschedule appointment
    if (task.type === "Appointment" && task.reschedule && !task.payment_required) {
      return (
        <Button
          onClick={() => {
            router.navigate({ to: `/dashboard/weight-loss/appointments/reschedule-booking?appointment_id=${task.appointment_id}`});
          }}
          className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        >
          Reschedule
        </Button>
      );
    }

    // Book appointment
    if (task.type === "Appointment" && !task.meeting_url && !task.payment_required && !task.completed) {
      return (
        <Button
          onClick={() => {
            router.navigate({ to: `/dashboard/weight-loss/appointments/booking`, });
          }}
          className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        >
          Book now
        </Button>
      );
    }

    // pay for appointment
    if (task.type === "Appointment" && task.payment_required && !task.reschedule) {
      return (
        <Button
          onClick={() => {
            if (!task.completed && task.appointment_id) {
              paymentCheckoutMutation.mutate({
                payment_type: task.type,
                type_id: task.appointment_id,
                success_url: successUrl(task.appointment_id),
                cancel_url: errorUrl(task.appointment_id),
              }, {
                onSuccess: (data) => {
                  if (data.success) {
                    window.location.href = data.data.url;
                  }
                },
                onError: (error) => {
                  console.error(error);
                }
              });
            }
          }}
          className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
          disabled={paymentCheckoutMutation.isPending}
        >
          Pay now
        </Button>
      );
    }

        // Complete consultation
    if (task.type === "Consultation" && task.payment_required) {
      return(
      <Button
        onClick={() => {
          createRegistrationPayment({
            name: `${auth?.user?.first_name} ${auth?.user?.last_name}`,
            email: auth?.user?.email,
            phone: auth?.user?.phone_number,
            success_url: consultSuccessUrl,
            cancel_url: consultCancelUrl,
            encounter_id: task.consultation_id,
          }, {
            onSuccess: (response) => {
            if (response.success) {
              window.location.replace(response.data?.payment_metadata.url || '');
            }
            },
            onError: () => {
            // Error handling
            }
          });
        }}
        className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        disabled={isPending}
      >
        {isPending ? (
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
        </div>
        ) : (
        "Pay now"
        )}
      </Button>
      )
    }

    // pay for prescription
    if (task.type === "Prescription" && task.payment_required) {
      return (
        <Button
          onClick={() => {
            if (!task.completed && task.prescription_id) {
              setPrescriptionId(task.prescription_id)
              setPayForMed(true)
            }
          }}
          className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        >
          Pay now
        </Button>
      );
    }

    // pay for refill
    if (task.type === "Refill" && task.payment_required) {
      return (
        <Button
          onClick={() => {
            console.log("Refill task", task)
            if (!task.completed && task.prescription_id) {
              setRefillPrescriptionId(task.prescription_id)
              setRefillMedicationId(task.medication_id)
              setPayForRefill(true)
            }
          }}
          className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        >
          Pay now
        </Button>
      );
    }

    // request for refill
    if (task.type === "Refill" && !task.payment_required) {
      return (
        <Button
          onClick={() => {
            if (!task.completed && task.prescription_id) {
              setReqPrescriptionId(task.prescription_id)
              setReqMedicationId(task.medication_id)
              setRequestRefill(true)
            }
          }}
          className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        >
          Request now
        </Button>
      );
    }

    // Complete questionnaire
    if (task.type === "Questionnaire") {
      return (
        <Button
          onClick={() => {
            router.navigate({ to: '/dashboard/onboarding' });
          }}
          className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        >
          Complete now
        </Button>
      );
    }

    return null;
  };

const renderAccordionContent = (task: Task) => {
  if (task.completed) {
    // Content for completed tasks
    return (
      <div className="space-y-4 w-full">
        <div className="space-y-2">
          <h3 className="text-base font-medium">What to expect next</h3>
          <ol className="list-decimal pl-5 space-y-1">
            {task.type === "Appointment" && (
              <>
                <li className="text-sm text-gray-700">Review and implement the discussed recommendations or action items.</li>
                <li className="text-sm text-gray-700">You will receive notifications and updates on your dashboard.</li>
                <li className="text-sm text-gray-700">You can contact support if you have any questions or concerns.</li>
              </>
            )}
            {task.type === "Consultation" && (
              <>
                <li className="text-sm text-gray-700">Our medical team will review your case and create a personalized plan.</li>
                <li className="text-sm text-gray-700">You will receive notifications and updates on your dashboard.</li>
                <li className="text-sm text-gray-700">You can contact support if you have any questions or concerns.</li>
              </>
            )}
            {(task.type === "Prescription" || task.type === "Refill") && (
              <>
                <li className="text-sm text-gray-700">Your order is now being processed and will be shipped shortly.</li>
                <li className="text-sm text-gray-700">You will receive notifications and updates on your dashboard.</li>
                <li className="text-sm text-gray-700">You can contact support if you have any questions or concerns.</li>
              </>
            )}
            {(task.type === "Questionnaire") && (
                            <>
                <li className="text-sm text-gray-700">Be prepared for a potential consultation or follow-up.</li>
                <li className="text-sm text-gray-700">You will receive notifications and updates on your dashboard.</li>
                <li className="text-sm text-gray-700">You can contact support if you have any questions or concerns.</li>
              </>
            )}
          </ol>
        </div>
        
        {/* Display additional information for completed tasks */}
        {(task.type === "Consultation" || task.type === "Appointment" || task.type === "Prescription" || task.type === "Refill") && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
            <div className="flex items-start gap-2">
              <Calendar className="size-5 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">Date initiated</p>
                <p className="text-sm font-medium">{task.when ? formatDate(task.when) : "24 April, 2025"}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <DollarSign className="size-5 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">Amount Paid:</p>
                <p className="text-sm font-medium">{"$50.00"}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <FileText className="size-5 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">
                  {task.type === "Consultation" || task.type === "Appointment" 
                    ? "Consultation Details:" 
                    : "Medications"}
                </p>
                <p className="text-sm font-medium">
                  {task.type === "Consultation" || task.type === "Appointment"
                    ? "Weight Loss Program Consultation" 
                    : "Ozempic, Vitamin C, Aderrall"}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  } else {
    // Content for pending tasks
    return (
      <div className="space-y-4 w-full">
        <div className="space-y-2">
          <h3 className="text-base font-medium">What to do</h3>
          <ol className="list-decimal pl-5 space-y-1">
            {task.type === "Consultation" && (
              <>
                <li className="text-sm text-gray-700">Pay now to start your consultation.</li>
                <li className="text-sm text-gray-700">Our team will review your case and create a personalized plan.</li>
                <li className="text-sm text-gray-700">You'll receive updates on your dashboard.</li>
              </>
            )}
            {task.type === "Prescription" && (
              <>
                <li className="text-sm text-gray-700">Complete your payment for the medication order to proceed.</li>
                <li className="text-sm text-gray-700">You'll receive updates on your dashboard.</li>
              </>
            )}
            {task.type === "Refill" && (
              <>
                <li className="text-sm text-gray-700">Complete your payment for the medication refill order to proceed.</li>
                <li className="text-sm text-gray-700">You'll receive updates on your dashboard.</li>
              </>
            )}
            {task.type === "Appointment" && task.meeting_url && (
              <>
                <li className="text-sm text-gray-700">Review any provided materials and come prepared with questions or topics to discuss</li>
                <li className="text-sm text-gray-700">Click the meeting link or dial-in number to join at the scheduled time</li>
              </>
            )}
            {task.type === "Appointment" && !task.meeting_url && !task.reschedule && (
              <>
                <li className="text-sm text-gray-700">Select a date and time that works for you</li>
                <li className="text-sm text-gray-700">Confirm your booking details</li>
              </>
            )}
            {task.type === "Appointment" && task.reschedule && (
              <>
                <li className="text-sm text-gray-700">Review any provided materials and come prepared with questions or topics to discuss</li>
                <li className="text-sm text-gray-700">Click the meeting link or dial-in number to join at the scheduled time</li>
              </>
            )}
          </ol>
        </div>
        
        <div className="pt-2">
          <p className="text-sm text-gray-700">
            Contact Support: Need help? Reach out to our support team appointments@proxymedicine.com
          </p>
        </div>
      </div>
    );
  }
};

  return (
    <main className="flex flex-col gap-6">
      <section className="py-10">
        {/* Greetings */}
        <span className="inline-flex gap-2 pb-4">
          <h6 className="text-lg font-normal">Welcome,</h6>
          <h6 className="text-lg font-bold">{user?.first_name}</h6>
        </span>
        {/* Subnav one */}

        <SubNav
          title="Home"
          buttonOne="Go to urgent care "
          buttonTwo="Book an appointment"
        />
        {/* Subnav Two - Accordion */}

        <Accordion
          type="single"
          collapsible
          value={activeItem}
          onValueChange={setActiveItem}
          className="w-full"
        >
          <AccordionItem
            value="user-details"
            className="mb-5 w-full rounded-2xl bg-white px-3 text-black shadow-gray-500"
          >

            <AccordionTrigger className="flex w-full justify-start gap-3 py-3 hover:no-underline sm:flex-row  sm:justify-between [&>svg:last-child]:hidden">
              <div className="flex flex-col space-y-2 sm:space-y-0 flex-1 sm:flex-row sm:items-center sm:justify-start">

                {/* Top Row: Avatar + Username */}
                <div className="flex items-center justify-start   gap-3">
                  <Avatar className="size-8">
                    <AvatarImage src={user?.avatar || ""} />
                    <AvatarFallback>{`${user?.first_name?.charAt(0)}${user?.last_name?.charAt(0)}`}</AvatarFallback>
                  </Avatar>
                  <h6 className="text-base font-bold sm:text-lg">{user?.first_name}{" "} {user?.last_name}</h6>
                </div>

                {/* Stats go under username on mobile, inline on desktop */}
                <div className="flex flex-wrap justify-start  sm:items-center sm:gap-y-0 gap-x-4  sm:pl-4">
                  <span className="inline-flex gap-1 text-xs sm:text-sm">
                    <h6 className="font-normal">Current Weight:</h6>
                    <h6 className="font-normal sm:font-bold sm:text-sm text-xs">{user?.health_profile?.weight} lbs</h6>
                  </span>
                  <span className="inline-flex gap-1 text-xs sm:text-sm ">
                    <h6 className="font-normal">Goal Weight:</h6>
                    <h6 className="font-normal sm:font-bold sm:text-sm text-xs">{user?.health_profile?.target_weight} lbs</h6>
                  </span>
                  <span className="inline-flex gap-1 text-xs my-3 sm:text-sm ">
                    <h6 className="font-normal">Time Frame:</h6>
                    <h6 className="font-normal sm:font-bold sm:text-sm text-xs">{user?.health_profile?.timeframe}</h6>
                  </span>
                </div>
              </div>

              {/* Chevron Icon – aligns right on desktop, centers on mobile */}

              <CircleChevronDown
                strokeWidth={0.75}
                className="size-8 text-[#7F8493] transition-transform duration-300 data-[state=open]:rotate-180"
              />

            </AccordionTrigger>
            <AccordionContent className="w-full px-4 py-3">
              <SnapshotCards
                conditions={medicalsConditionsQuery.data?.conditions
                  ?.map((condition) => condition?.name)
                  .join(", ") || ""}
                allergies={medicalsAllergiesQuery.data?.allergies
                  ?.map((allergy) => allergy?.name)
                  .join(", ") || ""}
                activeMeds={activeMedicationsQuery.data?.active_medications
                  ?.map((activeMed) => activeMed?.medication?.name)
                  .join(", ") || ""}
                refills={accordionItems.content.medicalSnapshot.refills}
                avgWeekLoss={accordionItems.content.wellnessStats.avgWeekLoss}
                workouts={accordionItems.content.wellnessStats.workouts}
                calorieBudget={
                  accordionItems.content.wellnessStats.calorieBudget
                }
                target={accordionItems.content.wellnessStats.target}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Timeline */}
        <div className="space-y-8">
          {sortedMonths.map((monthData, monthIndex) => (
            <div key={monthData.month} className="flex flex-col sm:flex-row">
              {/* Timeline Indicator - Hidden on mobile */}
              <div className="relative hidden w-10 flex-col items-center sm:flex">
                {completedStages[monthIndex] ? (
                  <Circle className="h-6 w-6 text-green-500" />
                ) : (
                  <>
                    <div className="relative flex size-10 items-center justify-center">
                      <Circle
                        className={`absolute size-7 ${monthIndex === 0 || completedStages[monthIndex - 1]
                          ? "text-[#4272DD]"
                          : "text-[#DCDCDE]"
                          }`}
                      />
                      <Circle
                        strokeWidth={5}
                        className={`absolute size-3 ${monthIndex === 0 || completedStages[monthIndex - 1]
                          ? "text-[#4272DD]"
                          : "text-[#DCDCDE]"
                          }`}
                      />
                    </div>

                    <div
                      className={`-mt-2 h-full w-1 ${monthIndex === 0 ? "bg-gradient-to-b from-blue-500 to-transparent" : "bg-gradient-to-b from-gray-300 to-transparent opacity-40"}`}
                    ></div>
                  </>

                )}
                {monthIndex < sortedMonths.length - 1 && (
                  <div
                    className={`-mt-2 h-full w-1 ${monthIndex === 0
                      ? "bg-gradient-to-b from-[#4272DD] to-transparent"
                      : "bg-gradient-to-b from-[#DCDCDE] to-transparent opacity-40"
                      }`}
                  ></div>
                )}
              </div>

              {/* Card Section */}
              <Card className="w-full">
                <CardContent className="p-0">
                  <div className="flex items-start justify-between gap-2 border-b px-6 pb-4 pt-3 sm:flex-row sm:items-center">
                    <h2 className="text-lg font-semibold">Month ({monthData.month})</h2>
                    <Badge
                      className={`rounded-2xl text-sm hover:bg-[#E1FAEA] ${completedStages[monthIndex]
                        ? "bg-[#E1FAEA] text-[#14AD4D]"
                        : "bg-[#E1FAEA] text-[#14AD4D]"
                        }`}
                    >
                      {completedStages[monthIndex] ? "Completed" : "Ongoing"}
                    </Badge>
                  </div>

                  {/* Tabs */}
                  <Tabs defaultValue="to-do">
                    <TabsList className="flex h-12 w-full flex-wrap justify-start gap-4 rounded-none border-b bg-background px-6 py-0">
                      <TabsTrigger
                        value="to-do"
                        className="h-full rounded-none border-b-2 border-transparent bg-background px-0 py-4 text-sm data-[state=active]:border-primary data-[state=active]:shadow-none"
                      >
                        To-do
                        <Badge className="ml-2 size-4 items-center justify-center rounded-full bg-[#FFAB1F] p-1 text-center text-xs">
                          {monthData.data.filter((task: Task) => !task.completed).length}
                        </Badge>
                      </TabsTrigger>
                      <TabsTrigger
                        value="completed"
                        className="h-full rounded-none border-b-2 border-transparent bg-background px-0 text-sm data-[state=active]:border-primary data-[state=active]:shadow-none"
                      >
                        Completed
                        <Badge className="ml-2 size-4 items-center justify-center rounded-full bg-[#FFAB1F] p-1 text-center text-xs">
                          {monthData.data.filter((task: Task) => task.completed).length}
                        </Badge>
                      </TabsTrigger>
                    </TabsList>

                    {/* To-do Tasks */}
                    <TabsContent value="to-do" className="px-6">
                      {monthData.data.length > 0 ? (
                        <>
                          {
                            monthData.data
                              .filter((task: Task) => !task.completed)
                              .length > 0 ? (
                                <Accordion
                                  type="single"
                                  collapsible
                                  value={activeTodoTask}
                                  onValueChange={setActiveTodoTask}
                                  className="w-full"
                                >
                                  {monthData.data
                                    .filter((task: Task) => !task.completed)
                                    .map((task: Task, index) => (
                                      <AccordionItem
                                        key={`todo-${monthData.month}-${index}`}
                                        value={`todo-${monthData.month}-${index}`}
                                        className="my-4 border rounded-[20px] shadow-sm"
                                      >
                                        <AccordionTrigger className="px-4 py-3 relative hover:no-underline [&>svg:last-child]:hidden">
                                          <div className="flex w-full flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-2">
                                            <div className="flex items-start gap-3 flex-1">
                                              <Circle
                                                strokeWidth={2}
                                                className="size-6 shrink-0 text-[#4272DD] mt-1 sm:mt-0"
                                              />
                                              <div className="flex text-left">
                                                <h3 className="text-base font-medium break-words">
                                                  {formatAppointmentText(task.title)}
                                                </h3>
                                                {task.type === "Appointment" && task.meeting_url && !task.payment_required && task.time ? (
                                                  <span className="text-sm text-[#2D2F35] flex items-center gap-2 flex-wrap mt-1">
                                                    <Clock size={16} className="text-[#7F8493]" />
                                                    <h6 className="text-xs text-[#202020CC]">
                                                      {`${formatFullDate(task.when)} ${formatTime(task?.time)}`}
                                                    </h6>
                                                  </span>
                                                ) : null}
                                                {/* {task.priority && (
                                                  <Badge className="h-6 w-16 mt-2 sm:hidden rounded-3xl bg-[#FFF1DA] text-[#9D6508] hover:bg-[#FFF1DA]">
                                                    Priority
                                                  </Badge>
                                                )} */}
                                              </div>
                                              {task.priority && (
                                                <Badge className="h-6 w-16 hidden sm:flex rounded-3xl bg-[#FFF1DA] text-[#9D6508] hover:bg-[#FFF1DA]">
                                                  Priority
                                                </Badge>
                                              )}
                                            </div>
                                            <div className="flex w-full sm:w-auto justify-start sm:justify-end">
                                              {renderTaskActionButton(task)}
                                            </div>
                                          </div>
                                          <CircleChevronDown
                                            strokeWidth={0.75}
                                            className="size-8 top-0 mt-3 md:mt-0 md:ml-2 right-0 mr-2 md:mr-0 text-[#7F8493] absolute md:relative transition-transform duration-300 data-[state=open]:rotate-180"
                                          />
                                        </AccordionTrigger>
                                        <AccordionContent className="px-4 py-2">
                                          <div className="flex mt-2">
                                            {renderAccordionContent(task)}
                                          </div>
                                        </AccordionContent>
                                      </AccordionItem>
                                    ))}
                                </Accordion>
                              ) : <EmptyTodo />}
                        </>
                      ) : (
                        <EmptyTodo />
                      )}
                    </TabsContent>

                    {/* Completed Tasks */}
                    <TabsContent value="completed" className="px-6">
                      {monthData.data
                        .filter((task: Task) => task.completed).length > 0 ? (
                          <Accordion
                            type="single"
                            collapsible
                            value={activeCompletedTask}
                            onValueChange={setActiveCompletedTask}
                            className="w-full"
                          >
                            {monthData.data
                              .filter((task: Task) => task.completed)
                              .map((task: Task, index) => (
                                <AccordionItem
                                  key={`completed-${monthData.month}-${index}`}
                                  value={`completed-${monthData.month}-${index}`}
                                  className="my-4 border rounded-[20px] shadow-sm bg-[#EBFBF1]"
                                >
                                  <AccordionTrigger className="px-4 py-3 hover:no-underline [&>svg:last-child]:hidden">
                                    <div className="flex w-full justify-between items-center">
                                      <div className="flex items-start gap-3">
                                        <div className="flex size-6 items-center justify-center rounded-full bg-[#14AD4D]">
                                          <Check
                                            className="size-3 text-white"
                                            strokeWidth={3}
                                          />
                                        </div>
                                        <div className="flex flex-col text-left">
                                          <h3 className="text-base font-medium">
                                            {task.title}
                                          </h3>
                                          <p className="text-sm text-gray-500">Completed</p>
                                        </div>
                                      </div>
                                    </div>
                                                                                            <CircleChevronDown
                strokeWidth={0.75}
                className="size-8 text-[#7F8493] transition-transform duration-300 data-[state=open]:rotate-180"
              />
                                  </AccordionTrigger>
                                  <AccordionContent className="px-4 py-2">
                                          <div className="flex  mt-2">
                                            {renderAccordionContent(task)}
                                          </div>
                                  </AccordionContent>
                                </AccordionItem>
                              ))}
                          </Accordion>
                        ) : <EmptyCompletedTodo />}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </section>

      {payForMed && prescriptionId && <MedicationPaymentModal
        prescription_id={prescriptionId}
        open={payForMed}
        onClose={() => setPayForMed(false)}
      />}

      {requestRefill && reqPrescriptionId && reqMedicationId && <MedicationRefillRequestModal
        prescription_id={reqPrescriptionId}
        medication_id={reqMedicationId}
        open={requestRefill}
        onClose={() => setRequestRefill(false)}
      />}

      {payForRefill && refillPrescriptionId && refillMedicationId && <MedicationRefillPaymentModal
        prescription_id={refillPrescriptionId}
        medication_id={refillMedicationId}
        open={payForRefill}
        onClose={() => setPayForRefill(false)}
      />}
    </main>
  );
}